import BasePeripheral from '@appblocks/codegeneration/Peripherals/_BasePeripheral';
import {
    SettingType, Setting, BlockType, ValidationMessage, ValidationMessageType,
    TibboDataType,
    SettingControlType,
} from '@appblocks/codegeneration/types';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';
import Peripherals from '@appblocks/codegeneration/Peripherals';
import BaseSPIPeripheral from './_BaseSPIPeripheral';
import BaseFeature from '../BaseFeature';

export default class Ili9342c extends BaseSPIPeripheral {
    constructor() {
        super();
        this.id = 'ili9342c';
        this.name = 'ILI9342C Display';
        this.requirements = ['zephyr'];

        const settings: Array<Setting> = [
            {
                id: 'GENERAL',
                name: 'General',
                settingType: SettingType.GROUP,
                default: '',
            },
            {
                id: 'IMAGES_GROUP',
                name: 'Images',
                settingType: SettingType.GROUP,
                default: '',
            },
            {
                id: 'RESETGPIOS',
                name: 'Reset GPIOs',
                settingType: SettingType.OPTION,
                required: true,
                group: 'GENERAL',
                refId: 'gpio',
                default: [],
            },
            {
                id: 'DCGPIOS',
                name: 'DC GPIOs',
                settingType: SettingType.OPTION,
                required: true,
                group: 'GENERAL',
                refId: 'gpio',
                default: [],
            },
            {
                id: 'LCD_SCREEN_WIDGET_COLUMNS',
                name: 'Width',
                settingType: SettingType.NUMBER,
                group: 'GENERAL',
                required: true,
                default: '0',
            },
            {
                id: 'LCD_SCREEN_WIDGET_ROWS',
                name: 'Height',
                settingType: SettingType.NUMBER,
                group: 'GENERAL',
                required: true,
                default: '0',
            },
            {
                id: 'ROTATION',
                name: 'Rotation',
                settingType: SettingType.NUMBER,
                group: 'GENERAL',
                required: true,
                default: '0',
            },
            {
                id: 'SCREENS',
                name: 'Screens',
                settingType: SettingType.ARRAY,
                required: true,
                group: 'GENERAL',
                refId: 'display_screen',
                default: new Array<BaseFeature>(0),
            },
            {
                id: 'IMAGES',
                name: 'Images',
                settingType: SettingType.ARRAY,
                refId: 'lcd_image',
                required: true,
                group: 'IMAGES_GROUP',
                default: new Array<BaseFeature>(0),
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getDependencies(): string[] {
        return [
            'display_lib',
        ];
    }

    getCodeBlocks() {
        const blocks = [];

        const project = this.project;
        const NAME = this.getSettingValue('NAME');
        const SPI = this.getSettingValue('SPI');
        if (!SPI) {
            return SPI;
        }
        const SPIFeature = project.getFeature(SPI);
        const LCD_SCREEN_WIDGET_COLUMNS = this.getSettingValue('LCD_SCREEN_WIDGET_COLUMNS');
        const LCD_SCREEN_WIDGET_ROWS = this.getSettingValue('LCD_SCREEN_WIDGET_ROWS');
        const ADDRESS = this.getSettingValue('ADDRESS');
        const ROTATION = this.getSettingValue('ROTATION');

        // const GND = this.getSettingValue('GND');
        // const VCC = this.getSettingValue('VCC');
        const SCREENS = this.getSettingValue('SCREENS');
        let dcGPIOSCode = '';

        const DCGPIOS = this.getSettingValue('DCGPIOS');

        if (DCGPIOS) {
            const items: string[] = [];
            const GPIOPIN = project.getFeature(DCGPIOS);
            const pin = Peripherals.getGPIOPeripheral(GPIOPIN.getSettingValue('PIN'), this.project.device.id);
            items.push(`<&${pin?.peripheralPort} ${pin.num} ${GPIOPIN.getFlags()}>`);

            dcGPIOSCode = `dc-gpios = ${items.join(', ')};`;
        }

        blocks.push({
            location: 'APP_OVERLAY_DTS',
            template: `
mipi_dbi {
    compatible = "zephyr,mipi-dbi-spi";
    ${dcGPIOSCode}
    // reset-gpios = <&axp192_gpio 4 (GPIO_OPEN_DRAIN | GPIO_ACTIVE_LOW)>;
    spi-dev = <&${SPIFeature.getSettingValue('PORT')}>;
    write-only;
    #address-cells = <1>;
    #size-cells = <0>;

    ili9342c: ili9342c@${ADDRESS} {
        compatible = "ilitek,ili9342c";
        mipi-max-frequency = <30000000>;
        reg = <${ADDRESS}>;
        // vin-supply = <&lcd_bg>;
        pixel-format = <ILI9XXX_PIXEL_FORMAT_RGB565>;
        // display-inversion;
        width = <${LCD_SCREEN_WIDGET_COLUMNS}>;
        height = <${LCD_SCREEN_WIDGET_ROWS}>;
        rotation = <${ROTATION}>;
    };
};
        `,
        });

        blocks.push({
            location: 'APP_OVERLAY_CHOSEN',
            template: `
zephyr,display = &ili9342c;`,
        });

        blocks.push({
            location: 'VARIABLE_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `const struct device *${NAME}_device;`,
        });

        return blocks;
    }
}
